// DERIVATIVES — Football workspace product. Game-prop edge picks at four
// odds tiers (@2, @3, @4, @10). Props only: corners, cards, bookings,
// fouls, throw-ins, offsides. NO goals O/U, NO BTTS, NO 1X2-adjacent.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  useDerivativesBoard,
  type DerivBoard,
  type DerivSlip,
  type DerivLeg,
  type DerivPoolLeg,
} from "./useDerivativesBoard.ts";
import "../../styles/derivatives.css";

/* ── Constants ────────────────────────────────────────────────────── */

const TIER_LABEL: Record<string, string> = { t2: "@2", t3: "@3", t4: "@4", t10: "@10" };
const TIER_ORDER = ["t2", "t3", "t4", "t10"] as const;

/* ── Helpers ──────────────────────────────────────────────────────── */

const fmtOdds = (o: number): string =>
  (typeof o === "number" && Number.isFinite(o) ? o : 0).toFixed(2);

const fmtBigOdds = (o: number): string => {
  if (!Number.isFinite(o) || o <= 0) return "—";
  if (o >= 1_000_000) return (o / 1_000_000).toFixed(1) + "M";
  if (o >= 1_000) return (o / 1_000).toFixed(1) + "K";
  if (o < 100) return o.toFixed(2);
  return o.toFixed(0);
};

const kickoffTime = (iso: string | null): string => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  }) + " GMT";
};

/* ── Count-up ─────────────────────────────────────────────────────── */

function useCountUp(target: number, opts: { duration?: number } = {}): number {
  const { duration } = opts;
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    const from = fromRef.current;
    const to = target;
    if (from === to) { setVal(to); return; }
    const mag = Math.abs(to);
    const dur = duration != null ? duration : mag >= 1e6 ? 1100 : mag >= 1e5 ? 850 : mag >= 1e3 ? 600 : 460;
    const t0 = performance.now();
    const ease = (p: number) => 1 - Math.pow(1 - p, 3);
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      setVal(from + (to - from) * ease(p));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else { fromRef.current = to; setVal(to); }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);
  return val;
}

/* ── Masthead ─────────────────────────────────────────────────────── */

function Masthead({ board }: { board: DerivBoard }): JSX.Element {
  const date = new Date(board.board_date + "T00:00:00Z").toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", timeZone: "UTC",
  });
  return (
    <div className="masthead">
      <div>
        <div className="mh-eyebrow">Football · Game Props</div>
        <h1 className="mh-title">DERIVATIVES</h1>
        <p className="mh-sub">
          Corners, cards, fouls, offsides — game-prop edge picks at four tiers.
          Individual picks are the product; the accumulator is a bonus assembly.
        </p>
      </div>
      <div className="mh-meta">
        <div className="mh-date">Scan date · <b>{date}</b></div>
        <div className="mh-status">
          <span className="pulse"></span>
          {board.pool_count > 0 ? "Live" : "Awaiting scan"} · {board.pool_count} legs in pool
        </div>
        <div className="mh-pills">
          <div className="mh-pill">
            <span className="k">Target</span>
            <span className="v green">10K–100K×</span>
          </div>
          <div className="mh-pill">
            <span className="k">Pool</span>
            <span className="v">{board.pool_count}</span>
          </div>
          {Object.entries(board.tier_counts ?? {}).map(([t, c]) => (
            <div key={t} className="mh-pill">
              <span className="k">{TIER_LABEL[t] ?? t}</span>
              <span className="v">{c as number}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── View switch ──────────────────────────────────────────────────── */

type ViewMode = "accumulator" | "pool";

function ViewSwitch({
  view, setView, slipCount, poolCount,
}: { view: ViewMode; setView: (v: ViewMode) => void; slipCount: number; poolCount: number }): JSX.Element {
  const opts: Array<{ id: ViewMode; label: string; cnt: number }> = [
    { id: "accumulator", label: "Accumulator", cnt: slipCount },
    { id: "pool", label: "Full Pool", cnt: poolCount },
  ];
  const idx = opts.findIndex((o) => o.id === view);
  return (
    <div className="tierbar">
      <div className="seg" role="tablist">
        <div className="seg-thumb" style={{ left: `calc(4px + ${idx} * (50% - 4px))`, width: "calc(50% - 4px)" }}></div>
        {opts.map((o) => (
          <button key={o.id} className={view === o.id ? "on" : ""} role="tab" aria-selected={view === o.id} onClick={() => setView(o.id)}>
            {o.label}<span className="cnt">{o.cnt}</span>
          </button>
        ))}
      </div>
      <div className="tier-caption">
        {view === "accumulator"
          ? <>The assembled accumulator — <b>derivative legs targeting 10K–100K×</b>.</>
          : <>Every scouted derivative leg — <b>filter by tier and scope</b>.</>}
      </div>
    </div>
  );
}

/* ── Leg row ──────────────────────────────────────────────────────── */

function EdgeBadge({ edge }: { edge: number | null | undefined }): JSX.Element | null {
  if (edge === null || edge === undefined) return null;
  const pct = (edge * 100).toFixed(1);
  if (edge > 0) return <span className="edge-badge pos">+{pct}%</span>;
  return <span className="edge-badge neg">{pct}%</span>;
}

function LegRow({ leg, rank }: { leg: DerivLeg; rank: number }): JSX.Element {
  return (
    <div className="leg">
      <div className="leg-rank">{String(rank).padStart(2, "0")}</div>
      <div className="leg-main">
        <div className="leg-fix">
          {leg.home_team}<span className="vs">v</span>{leg.away_team}
        </div>
        <div className="leg-sub">
          <span>{leg.league}</span>
          <span className="d"></span>
          <span className="mono">{kickoffTime(leg.kickoff_at)}</span>
        </div>
        <div className="leg-pick">
          <span className="pred">{leg.pick}</span>
          <span className="mkt">{leg.market_label}</span>
          <EdgeBadge edge={leg.edge} />
        </div>
      </div>
      <span className={`leg-tier ${leg.odds_tier}`}>{TIER_LABEL[leg.odds_tier]}</span>
      <div className="leg-odds">
        <div className="o">{fmtOdds(leg.odds)}</div>
      </div>
    </div>
  );
}

/* ── Ticket ───────────────────────────────────────────────────────── */

function Ticket({
  slip, stake, setStake,
}: { slip: DerivSlip; stake: number; setStake: (n: number) => void }): JSX.Element {
  const winnings = useCountUp(stake * slip.combined_odds);
  const odds = useCountUp(slip.combined_odds);
  const chips = [1, 5, 10, 25];

  const tierBreakdown = useMemo(() => {
    const m: Record<string, number> = {};
    for (const l of slip.legs) {
      m[l.odds_tier] = (m[l.odds_tier] ?? 0) + 1;
    }
    return m;
  }, [slip.legs]);

  return (
    <div className="ticket">
      <div className="ticket-head">
        <span className="tk">Derivatives</span>
        <span className="tn">{slip.leg_count}-leg accumulator</span>
      </div>
      <div className="ticket-body">
        <div className="tk-block">
          <div className="tk-lbl">Combined total odds</div>
          <div className="tk-odds">
            <span className="x">×</span>{fmtBigOdds(odds)}
          </div>
          <div className="tk-target">Target: 10,000 – 100,000×</div>
        </div>
        <div className="tk-block">
          <div className="tk-lbl">Your stake</div>
          <div className="tk-input-wrap">
            <span className="pfx">$</span>
            <input
              className="tk-input mono"
              type="number"
              min="0"
              inputMode="decimal"
              value={stake}
              onChange={(e) => setStake(Math.max(0, Number(e.target.value) || 0))}
            />
          </div>
          <div className="tk-chips">
            {chips.map((c) => (
              <button key={c} className={`tk-chip ${stake === c ? "on" : ""}`} onClick={() => setStake(c)}>
                ${c}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="tk-win">
        <div className="tk-lbl">Potential winnings</div>
        <div className="tk-win-val">
          <span className="cur">$</span>
          {Math.round(winnings).toLocaleString("en-US")}
        </div>
      </div>
      <div className="tk-meta">
        <div className="m"><div className="k">Legs</div><div className="v">{slip.leg_count}</div></div>
        {TIER_ORDER.map((t) => (
          tierBreakdown[t] ? (
            <div key={t} className="m">
              <div className="k">{TIER_LABEL[t]} legs</div>
              <div className="v">{tierBreakdown[t]}</div>
            </div>
          ) : null
        ))}
      </div>
      <div className="tk-risk">
        <span className="tag">Note</span>
        <span>
          Game props (corners, cards, fouls) carry higher variance than core markets.
          The desk surfaces the picks; the call is yours.
        </span>
      </div>
      <div className="tk-foot">Read-only intelligence · no bet placement</div>
    </div>
  );
}

/* ── Accumulator view ─────────────────────────────────────────────── */

function AccumulatorView({
  slip, stake, setStake,
}: { slip: DerivSlip | null; stake: number; setStake: (n: number) => void }): JSX.Element {
  if (!slip || slip.legs.length === 0) {
    return (
      <div className="state-wrap surface-enter">
        <div className="state-title">No accumulator assembled yet</div>
        <div className="state-body">
          The scout runs daily. Once enough derivative legs are in the pool,
          the assembler builds an accumulator targeting 10K–100K× combined odds.
        </div>
      </div>
    );
  }

  return (
    <div className="acca-grid surface-enter">
      <div className="panel">
        <div className="panel-head">
          <span className="pk">Accumulator</span>
          <span className="pt">{slip.leg_count} legs</span>
          <span className="pr">×{fmtBigOdds(slip.combined_odds)} combined</span>
        </div>
        {slip.legs.map((leg, i) => (
          <LegRow key={leg.id ?? i} leg={leg} rank={i + 1} />
        ))}
      </div>
      <Ticket slip={slip} stake={stake} setStake={setStake} />
    </div>
  );
}

/* ── Pool view ────────────────────────────────────────────────────── */

function PoolView({ pool }: { pool: DerivPoolLeg[] }): JSX.Element {
  const [tierFilter, setTierFilter] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!tierFilter) return pool;
    return pool.filter((l) => l.odds_tier === tierFilter);
  }, [pool, tierFilter]);

  if (pool.length === 0) {
    return (
      <div className="state-wrap surface-enter">
        <div className="state-title">Pool is empty</div>
        <div className="state-body">
          The daily scout hasn't run yet, or no derivative markets are available for today's fixtures.
        </div>
      </div>
    );
  }

  return (
    <div className="surface-enter">
      <div className="tier-filter">
        <button
          className={`tier-btn ${tierFilter === null ? "on" : ""}`}
          onClick={() => setTierFilter(null)}
        >
          All ({pool.length})
        </button>
        {TIER_ORDER.map((t) => {
          const cnt = pool.filter((l) => l.odds_tier === t).length;
          if (cnt === 0) return null;
          return (
            <button
              key={t}
              className={`tier-btn ${tierFilter === t ? "on" : ""}`}
              onClick={() => setTierFilter(tierFilter === t ? null : t)}
            >
              {TIER_LABEL[t]} ({cnt})
            </button>
          );
        })}
      </div>

      <div className="pool-head">
        <div>Tier</div>
        <div>Fixture</div>
        <div>Pick</div>
        <div>Market</div>
        <div className="r">Edge</div>
        <div className="r">Odds</div>
      </div>
      {filtered.map((leg) => (
        <div key={leg.id} className="pool-row">
          <div className="pr-tier">
            <span className={`leg-tier ${leg.odds_tier}`}>{TIER_LABEL[leg.odds_tier]}</span>
          </div>
          <div className="pr-fix">
            <div className="t">{leg.home_team}<span className="vs">v</span>{leg.away_team}</div>
            <div className="s">{leg.league} · {kickoffTime(leg.kickoff_at)}</div>
          </div>
          <div className="pr-pick">{leg.pick}</div>
          <div className="pr-mkt">{leg.market_label}</div>
          <div className="pr-edge"><EdgeBadge edge={leg.edge} /></div>
          <div className="pr-odds mono">{fmtOdds(leg.odds)}</div>
        </div>
      ))}
    </div>
  );
}

/* ── States ───────────────────────────────────────────────────────── */

function LoadingState(): JSX.Element {
  return (
    <div>
      <div className="tierbar">
        <div className="sk" style={{ width: 240, height: 46, borderRadius: 8 }}></div>
      </div>
      <div className="acca-grid">
        <div className="panel">
          <div className="sk" style={{ height: 50, margin: 0, borderRadius: 0 }}></div>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="sk sk-row" style={{ margin: "8px 16px" }}></div>
          ))}
        </div>
        <div className="sk" style={{ height: 420, borderRadius: 12 }}></div>
      </div>
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="state-wrap surface-enter">
      <div className="state-title">No derivatives board yet</div>
      <div className="state-body">
        The daily scout runs at 06:00 UTC. Once it finds derivative-market legs
        across today's fixtures, the board appears here.
      </div>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────── */

export function DerivativesPage(): JSX.Element {
  const { loading, data: board, error } = useDerivativesBoard();
  const [view, setView] = useState<ViewMode>("accumulator");
  const [stake, setStake] = useState(5);

  // Pick domestic slip by default, fall back to international
  const activeSlip = board?.domestic ?? board?.international ?? null;
  const slipLegCount = activeSlip?.leg_count ?? 0;
  const poolCount = board?.pool_count ?? 0;

  let content;
  if (loading) {
    content = <LoadingState />;
  } else if (error) {
    content = (
      <div className="state-wrap">
        <div className="state-title">Error</div>
        <div className="state-body" style={{ color: "var(--danger-text)" }}>{error}</div>
      </div>
    );
  } else if (!board || poolCount === 0) {
    content = <EmptyState />;
  } else {
    content = (
      <>
        <ViewSwitch
          view={view}
          setView={setView}
          slipCount={slipLegCount}
          poolCount={poolCount}
        />
        {view === "accumulator" ? (
          <AccumulatorView slip={activeSlip} stake={stake} setStake={setStake} />
        ) : (
          <PoolView pool={board.pool ?? []} />
        )}
      </>
    );
  }

  return (
    <div className="deriv-page">
      {board && <Masthead board={board} />}
      {content}
    </div>
  );
}
